import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Users, Plus, Search, Key, ToggleLeft, ToggleRight, Loader2, Copy, Shield,
  Trash2, AlertTriangle, CheckCircle2, Mail, Pencil, Save,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { maskTelefone, validaTelefone } from "../lib/validators";

const ROLE_BADGE = {
  diretor: "bg-red-100 text-red-700",
  gerente: "bg-orange-100 text-orange-700",
  supervisao: "bg-purple-100 text-purple-700",
  financeiro: "bg-emerald-100 text-emerald-700",
  vendedor: "bg-blue-100 text-blue-700",
  operador: "bg-cyan-100 text-cyan-700",
  oficina: "bg-amber-100 text-amber-700",
  cliente: "bg-zinc-100 text-zinc-700",
  admin_templates: "bg-violet-100 text-violet-700",
};

const ROLES_DISPONIVEIS = [
  ["diretor", "Diretor"], ["gerente", "Gerente"], ["supervisao", "Supervisão"],
  ["financeiro", "Financeiro"], ["vendedor", "Vendedor"],
  ["operador", "Operador"], ["oficina", "Oficina"], ["cliente", "Cliente"],
  ["admin_templates", "Admin de Templates"],
];

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).then(() => toast.success("Copiado!"));
}

// ============================================================
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function ModalNovoUsuario({ open, onClose, onCreated, usuariosExistentes, usuarioEdicao }) {
  const editMode = !!usuarioEdicao;
  const [form, setForm] = useState({
    name: "", email: "", telefone: "", role: "operador", tipo: "interno",
    exigir_troca_senha: true, permitir_google: true, ativo: true,
    observacao: "", cliente_locativo_id: null, senha_temporaria: "",
    unidade_id: "", escopo: "unidade",
  });
  const [clienteQuery, setClienteQuery] = useState("");
  const [clienteOpts, setClienteOpts] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [nomeWarning, setNomeWarning] = useState("");
  const [unidadesOpts, setUnidadesOpts] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/comercial/unidades").then((r) => setUnidadesOpts(r.data || [])).catch(() => setUnidadesOpts([]));
    }
  }, [open]);

  // Reset/preenche quando abrir
  useEffect(() => {
    if (open) {
      setEmailError("");
      setNomeWarning("");
      if (editMode && usuarioEdicao) {
        setForm({
          name: usuarioEdicao.name || "",
          email: usuarioEdicao.email || "",
          telefone: usuarioEdicao.telefone || "",
          role: usuarioEdicao.role || "operador",
          tipo: usuarioEdicao.tipo || "interno",
          exigir_troca_senha: false,
          permitir_google: usuarioEdicao.permitir_google ?? true,
          ativo: usuarioEdicao.ativo ?? true,
          observacao: usuarioEdicao.observacao || "",
          cliente_locativo_id: usuarioEdicao.cliente_locativo_id || null,
          senha_temporaria: "",
          unidade_id: usuarioEdicao.unidade_id || "",
          escopo: usuarioEdicao.escopo || "unidade",
        });
        // Iter66 — pré-carrega cliente vinculado de qualquer origem (locativo OU crm)
        if (usuarioEdicao.cliente_crm_id) {
          setClienteSel({
            id: usuarioEdicao.cliente_crm_id,
            origem: "crm",
            nome_fantasia: usuarioEdicao.cliente_crm_nome || usuarioEdicao.cliente_locativo_nome || "Cliente vinculado",
            cnpj: usuarioEdicao.cliente_crm_cnpj || usuarioEdicao.cliente_locativo_cnpj || "",
            uf: "",
          });
        } else if (usuarioEdicao.cliente_locativo_id) {
          setClienteSel({
            id: `loc_${usuarioEdicao.cliente_locativo_id}`,
            origem: "locativo",
            nome_fantasia: usuarioEdicao.cliente_locativo_nome || "Cliente vinculado",
            cnpj: usuarioEdicao.cliente_locativo_cnpj || "",
            uf: "",
          });
        } else {
          setClienteSel(null);
        }
      } else {
        setForm({
          name: "", email: "", telefone: "", role: "operador", tipo: "interno",
          exigir_troca_senha: true, permitir_google: true, ativo: true,
          observacao: "", cliente_locativo_id: null, senha_temporaria: "",
          unidade_id: "", escopo: "unidade",
        });
        setClienteSel(null);
      }
      setClienteQuery("");
    }
  }, [open, editMode, usuarioEdicao]);

  // Validação inline de e-mail (formato + duplicidade local) — pulada em modo edição (email readonly)
  useEffect(() => {
    if (editMode) { setEmailError(""); return; }
    if (!form.email) { setEmailError(""); return; }
    if (/\s/.test(form.email)) { setEmailError("E-mail não pode conter espaços"); return; }
    if (!EMAIL_REGEX.test(form.email)) { setEmailError("Formato de e-mail inválido"); return; }
    const lower = form.email.toLowerCase();
    const dup = (usuariosExistentes || []).find((u) => (u.email || "").toLowerCase() === lower);
    if (dup) { setEmailError(`Este e-mail já está cadastrado (${dup.name})`); return; }
    setEmailError("");
  }, [form.email, usuariosExistentes, editMode]);

  // Alerta (não bloqueia) quando nome idêntico já existe — pulado em edição se mesmo usuário
  useEffect(() => {
    const n = (form.name || "").trim().toLowerCase();
    if (n.length < 3) { setNomeWarning(""); return; }
    const dup = (usuariosExistentes || []).find(
      (u) => (u.name || "").trim().toLowerCase() === n && (!editMode || u.id !== usuarioEdicao?.id),
    );
    setNomeWarning(dup ? `Já existe um usuário com este nome (${dup.email}). Confirma o cadastro?` : "");
  }, [form.name, usuariosExistentes, editMode, usuarioEdicao]);

  useEffect(() => {
    if (form.tipo === "cliente" && clienteQuery.trim().length >= 2) {
      const t = setTimeout(() => {
        // Iter66 — endpoint unificado: busca em clientes_locativo E clientes (CRM)
        api.get("/clientes-busca", { params: { q: clienteQuery, limit: 12 } })
          .then((r) => {
            // Normaliza para o formato interno (mantém shape usado no render)
            const items = (r.data?.items || []).map((c) => ({
              id: c.id,                       // "loc_XX" ou uuid do CRM
              origem: c.origem,                // "locativo" | "crm"
              nome_fantasia: c.nome,
              cnpj: c.cnpj_cpf,
              uf: c.uf || "",
              cidade: "",
              tipo_cliente: c.tipo_cliente,
            }));
            setClienteOpts(items);
          })
          .catch(() => setClienteOpts([]));
      }, 300);
      return () => clearTimeout(t);
    } else {
      setClienteOpts([]);
    }
  }, [clienteQuery, form.tipo]);

  const setRole = (v) => {
    const tipo = v === "cliente" ? "cliente" : "interno";
    setForm({ ...form, role: v, tipo });
  };

  // Mutex visual: se Google ON, sugerir exigir_troca OFF (mas não força)
  const toggleGoogle = (checked) => {
    setForm((f) => ({
      ...f,
      permitir_google: checked,
      // se ligou Google, sugere desligar troca obrigatória (login será via Google)
      exigir_troca_senha: checked ? false : f.exigir_troca_senha,
    }));
  };

  // Sprint A + Auditoria — perfis comerciais e operacionais exigem unidade_id
  // Alinhado com backend (admin_usuarios_routes.py linha ~101)
  // Exceção: diretor/supervisao com escopo=geral (tem visão total)
  const exigeUnidade =
    ["vendedor", "diretor", "supervisao", "gerente", "operador", "oficina", "financeiro"].includes(form.role) &&
    !(["diretor", "supervisao"].includes(form.role) && form.escopo === "geral");

  const podeEnviar =
    form.name.trim().length >= 2 &&
    (editMode || form.email.trim().length > 0) &&
    !emailError &&
    form.role &&
    !busy &&
    (form.tipo !== "cliente" || !!clienteSel) &&
    (!exigeUnidade || !!form.unidade_id);

  // Helpers para extrair ID conforme a origem do cliente selecionado (Iter66)
  const extrairClienteIds = () => {
    if (form.tipo !== "cliente" || !clienteSel) {
      return { cliente_locativo_id: null, cliente_crm_id: null };
    }
    if (clienteSel.origem === "locativo") {
      // id vem como "loc_123" → int(123)
      const raw = String(clienteSel.id || "").replace(/^loc_/, "");
      const asInt = parseInt(raw, 10);
      return {
        cliente_locativo_id: Number.isFinite(asInt) ? asInt : null,
        cliente_crm_id: null,
      };
    }
    if (clienteSel.origem === "crm") {
      return { cliente_locativo_id: null, cliente_crm_id: clienteSel.id || null };
    }
    // Fallback: assumir locativo (compat com dados antigos numeric-only)
    const asInt = parseInt(clienteSel.id, 10);
    return {
      cliente_locativo_id: Number.isFinite(asInt) ? asInt : null,
      cliente_crm_id: null,
    };
  };

  const submit = async () => {
    if (!podeEnviar) return;
    setBusy(true);
    try {
      const { cliente_locativo_id, cliente_crm_id } = extrairClienteIds();
      if (editMode) {
        // PATCH — apenas campos editáveis
        const patchPayload = {
          name: form.name,
          telefone: form.telefone || null,
          role: form.role,
          permitir_google: form.permitir_google,
          observacao: form.observacao || null,
          cliente_locativo_id,
          cliente_crm_id,
          unidade_id: form.escopo === "geral" ? null : (form.unidade_id || null),
          escopo: ["diretor", "supervisao"].includes(form.role) ? form.escopo : null,
        };
        await api.patch(`/admin/usuarios/${usuarioEdicao.id}`, patchPayload);
        toast.success(`Usuário ${usuarioEdicao.email} atualizado`);
        onCreated();
        onClose();
        return;
      }
      // CREATE
      const payload = {
        ...form,
        email: form.email.trim().toLowerCase(),
        cliente_locativo_id,
        cliente_crm_id,
        senha_temporaria: form.senha_temporaria || null,
        unidade_id: form.escopo === "geral" ? null : (form.unidade_id || null),
        escopo: ["diretor", "supervisao"].includes(form.role) ? form.escopo : null,
      };
      const { data } = await api.post("/admin/usuarios", payload);
      toast.success(`Usuário ${data.email} cadastrado com sucesso`);
      if (data.senha_temporaria_plain) {
        toast.message("Senha temporária gerada (mostrada apenas uma vez)", {
          description: data.senha_temporaria_plain,
          duration: 20000,
          action: { label: "Copiar", onClick: () => copyToClipboard(data.senha_temporaria_plain) },
        });
      }
      // Reset + fecha + atualiza lista
      onCreated();
      onClose();
      setForm({ name: "", email: "", telefone: "", role: "operador", tipo: "interno",
        exigir_troca_senha: true, permitir_google: true, ativo: true,
        observacao: "", cliente_locativo_id: null, senha_temporaria: "",
        unidade_id: "", escopo: "unidade" });
      setClienteSel(null); setClienteQuery("");
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409) {
        setEmailError(typeof detail === "string" ? detail : "E-mail já cadastrado");
        toast.error("Não foi possível cadastrar: e-mail já existe");
        // NÃO fecha modal
      } else if (status === 422) {
        toast.error(typeof detail === "string" ? detail : "Dados inválidos. Verifique os campos.");
      } else {
        toast.error(typeof detail === "string" ? detail : (editMode ? "Erro ao atualizar usuário" : "Erro ao cadastrar usuário"));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={editMode ? "modal-editar-usuario" : "modal-novo-usuario"}>
        <DialogHeader>
          <DialogTitle>{editMode ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
          <DialogDescription>
            {editMode
              ? "Atualize perfil, unidade, escopo e demais campos. E-mail e senha não são editáveis aqui."
              : "Crie um usuário interno ou cliente. Senha temporária é gerada automaticamente."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Nome completo *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                minLength={2}
                data-testid="novo-nome"
                className={nomeWarning ? "border-amber-400" : ""}
              />
              {form.name && form.name.trim().length < 2 && (
                <p className="text-[11px] text-red-600 mt-1">Mínimo 2 caracteres</p>
              )}
              {nomeWarning && (
                <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1" data-testid="nome-warning">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {nomeWarning}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">E-mail *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value.toLowerCase().replace(/\s/g, "")})}
                data-testid="novo-email"
                disabled={editMode}
                className={`${emailError ? "border-red-500 focus-visible:ring-red-400" : ""} ${editMode ? "bg-zinc-100 text-zinc-500" : ""}`}
              />
              {emailError && (
                <p className="text-[11px] text-red-600 mt-1 flex items-start gap-1" data-testid="email-error">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {emailError}
                </p>
              )}
              {!emailError && form.email && EMAIL_REGEX.test(form.email) && (
                <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> E-mail válido
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Telefone</Label>
              <Input
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: maskTelefone(e.target.value) })}
                placeholder="(00) 00000-0000"
                data-testid="novo-telefone"
                className={form.telefone && !validaTelefone(form.telefone) ? "border-red-500" : ""}
              />
              {form.telefone && !validaTelefone(form.telefone) && (
                <p className="text-[11px] text-red-600 mt-1" data-testid="telefone-error">Telefone inválido (DDD 11-99)</p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Perfil</Label>
              <Select value={form.role} onValueChange={setRole}>
                <SelectTrigger data-testid="novo-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES_DISPONIVEIS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.tipo === "cliente" && (
            <div className="border rounded-md p-3 bg-blue-50">
              <Label className="text-xs uppercase tracking-wide text-blue-700">Cliente vinculado *</Label>
              {clienteSel ? (
                <div className="flex justify-between items-center mt-1 p-2 bg-white rounded">
                  <div className="text-sm">
                    <strong>{clienteSel.nome_fantasia}</strong>
                    {clienteSel.origem && (
                      <span className={`ml-2 text-[9px] uppercase font-mono px-1.5 py-0.5 rounded ${
                        clienteSel.origem === "crm"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}>
                        {clienteSel.origem === "crm" ? "CRM" : "Locativo"}
                      </span>
                    )}
                    <div className="text-xs text-zinc-500">{clienteSel.cnpj || "—"}{clienteSel.uf ? ` · ${clienteSel.uf}` : ""}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setClienteSel(null)} data-testid="novo-cliente-trocar">Trocar</Button>
                </div>
              ) : (
                <>
                  <Input value={clienteQuery} onChange={(e) => setClienteQuery(e.target.value)} placeholder="Nome, CNPJ (com ou sem máscara) ou e-mail..." className="mt-1" data-testid="novo-cliente-search" />
                  {clienteOpts.length > 0 && (
                    <div className="border rounded mt-1 bg-white max-h-40 overflow-auto">
                      {clienteOpts.map((c) => (
                        <button key={`${c.origem}-${c.id}`} type="button" onClick={() => setClienteSel(c)}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-50 border-b last:border-0 flex items-center justify-between gap-2"
                          data-testid={`novo-cliente-opt-${c.id}`}>
                          <span className="truncate">
                            <strong>{c.nome_fantasia}</strong>
                            <span className="text-zinc-500"> · {c.cnpj || "—"}</span>
                            {c.uf && <span className="text-zinc-500"> · {c.uf}</span>}
                          </span>
                          <span className={`text-[9px] uppercase font-mono px-1.5 py-0.5 rounded shrink-0 ${
                            c.origem === "crm"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}>
                            {c.origem === "crm" ? "CRM" : "Locativo"}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {clienteQuery.trim().length >= 2 && clienteOpts.length === 0 && (
                    <p className="text-[11px] text-zinc-500 mt-1" data-testid="novo-cliente-empty">
                      Nenhum cliente encontrado para "{clienteQuery}"
                    </p>
                  )}
                </>
              )}
            </div>
          )}
          {form.tipo !== "cliente" && (
            <div className="border rounded-md p-3 bg-emerald-50/50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide">
                    Unidade de atuação {exigeUnidade && "*"}
                  </Label>
                  <Select
                    value={form.unidade_id || "_none"}
                    onValueChange={(v) => setForm({ ...form, unidade_id: v === "_none" ? "" : v })}
                    disabled={form.escopo === "geral" && ["diretor","supervisao"].includes(form.role)}
                  >
                    <SelectTrigger data-testid="novo-unidade"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— (nenhuma)</SelectItem>
                      {unidadesOpts.filter((u) => u.ativa).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.nome} {u.estado ? `(${u.estado})` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {exigeUnidade && !form.unidade_id && (
                    <p className="text-[11px] text-red-600 mt-1">Unidade obrigatória para este perfil</p>
                  )}
                </div>
                {["diretor", "supervisao"].includes(form.role) && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide">Escopo do diretor</Label>
                    <Select value={form.escopo} onValueChange={(v) => setForm({ ...form, escopo: v })}>
                      <SelectTrigger data-testid="novo-escopo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unidade">Diretor de Unidade (apenas sua unidade)</SelectItem>
                        <SelectItem value="geral">Diretor Geral (todas as unidades)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}
          {!editMode && (
            <div>
              <Label className="text-xs uppercase tracking-wide">Senha temporária (opcional — auto-gerada se vazio)</Label>
              <Input value={form.senha_temporaria} onChange={(e) => setForm({...form, senha_temporaria: e.target.value})} placeholder="Deixe vazio para gerar" />
            </div>
          )}
          <div className={`grid ${editMode ? "grid-cols-1" : "grid-cols-3"} gap-2 text-xs`}>
            {!editMode && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.exigir_troca_senha} onChange={(e) => setForm({...form, exigir_troca_senha: e.target.checked})} />
                Exigir troca no 1º login
              </label>
            )}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.permitir_google}
                onChange={(e) => toggleGoogle(e.target.checked)}
              />
              Permitir login Google
            </label>
            {!editMode && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({...form, ativo: e.target.checked})} />
                Ativo
              </label>
            )}
          </div>
          {!editMode && form.permitir_google && form.exigir_troca_senha && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              ⚠ Usuário poderá entrar por Google OU senha temporária. Se for acesso somente via Google,
              desmarque "Exigir troca no 1º login" — assim nenhuma senha temporária será exibida.
            </p>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wide">Observação interna</Label>
            <Textarea value={form.observacao} onChange={(e) => setForm({...form, observacao: e.target.value})} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} disabled={!podeEnviar} data-testid="novo-confirmar" className="bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50">
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : editMode ? (
              <Save className="w-4 h-4 mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {editMode ? "Salvar alterações" : "Criar usuário"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
export default function AdminUsuarios() {
  const { user: currentUser } = useAuth();
  const isDiretor = currentUser?.role === "diretor";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [tipo, setTipo] = useState("");
  const [ativo, setAtivo] = useState("");
  const [page, setPage] = useState(0);
  const [novoOpen, setNovoOpen] = useState(false);
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  const limit = 25;

  const carregar = async () => {
    setLoading(true);
    try {
      const params = { q, limit, offset: page * limit };
      if (role) params.role = role;
      if (tipo) params.tipo = tipo;
      if (ativo !== "") params.ativo = ativo === "true";
      const { data } = await api.get("/admin/usuarios", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  useEffect(() => { carregar(); }, [q, role, tipo, ativo, page]);

  const resetSenha = async (u) => {
    if (!window.confirm(`Resetar senha de ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/usuarios/${u.id}/reset-senha`, { exigir_troca: true });
      toast.message("Senha resetada", {
        description: data.senha_temporaria_plain,
        duration: 15000,
        action: { label: "Copiar", onClick: () => copyToClipboard(data.senha_temporaria_plain) },
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleStatus = async (u) => {
    const novo = !u.ativo;
    if (!window.confirm(`${novo ? "Ativar" : "Desativar"} ${u.email}?`)) return;
    try {
      await api.patch(`/admin/usuarios/${u.id}/status`, { ativo: novo });
      toast.success(`Usuário ${novo ? "ativado" : "desativado"}`);
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const excluirUsuario = async (u) => {
    if (!window.confirm(
      `EXCLUSÃO DEFINITIVA\n\nTem certeza que deseja apagar o usuário ${u.name} (${u.email})?\n\nEssa ação não pode ser desfeita.`
    )) return;
    try {
      await api.delete(`/admin/usuarios/${u.id}`);
      toast.success(`Usuário ${u.email} excluído`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir usuário");
    }
  };

  const reenviarConvite = async (u) => {
    if (!window.confirm(`Reenviar convite por e-mail para ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/usuarios/${u.id}/reenviar-convite`);
      toast.success(`Convite reenviado (${data.email_enviado})`, { duration: 5000 });
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reenviar convite");
    }
  };

  const renovarAcesso = async (u) => {
    const dias = window.prompt(`Renovar acesso de ${u.email}\n\nQuantos dias adicionar (padrão 60)?`, "60");
    if (!dias) return;
    const n = parseInt(dias, 10);
    if (isNaN(n) || n <= 0) return toast.error("Número de dias inválido");
    try {
      const { data } = await api.patch(`/admin/usuarios/${u.id}/renovar-acesso`, { dias: n });
      toast.success(`Acesso renovado até ${new Date(data.data_expiracao).toLocaleDateString("pt-BR")}`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao renovar acesso");
    }
  };

  // Helper para formatar e classificar data de expiração
  const formatExpira = (iso) => {
    if (!iso) return { txt: "—", cls: "text-zinc-400", expirada: false };
    const d = new Date(iso);
    if (isNaN(d.getTime())) return { txt: "—", cls: "text-zinc-400", expirada: false };
    const diff = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    const txt = d.toLocaleDateString("pt-BR");
    if (diff < 0) return { txt, cls: "text-red-600 font-semibold", expirada: true };
    if (diff < 7) return { txt, cls: "text-amber-600 font-semibold", expirada: false, alerta: true };
    return { txt, cls: "text-zinc-600", expirada: false };
  };

  return (
    <div className="space-y-4" data-testid="page-admin-usuarios">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-orange-600" /> Administração · Usuários</h1>
          <p className="text-xs text-zinc-500">{total} usuários no sistema · gestão de acessos e perfis</p>
        </div>
        {isDiretor && (
          <Button onClick={() => setNovoOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="btn-novo-usuario">
            <Plus className="w-4 h-4 mr-1" /> Novo Usuário
          </Button>
        )}
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Buscar por nome ou e-mail..." className="pl-9" data-testid="admin-search" />
          </div>
          <Select value={role || "all"} onValueChange={(v) => { setRole(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger data-testid="admin-filter-role"><SelectValue placeholder="Perfil" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos perfis</SelectItem>
              {ROLES_DISPONIVEIS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tipo || "all"} onValueChange={(v) => { setTipo(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos tipos</SelectItem>
              <SelectItem value="interno">Interno</SelectItem>
              <SelectItem value="cliente">Cliente</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ativo || "all"} onValueChange={(v) => { setAtivo(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="true">Ativos</SelectItem>
              <SelectItem value="false">Inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="tabela-usuarios">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead>NOME / E-MAIL</TableHead>
              <TableHead className="w-32">PERFIL</TableHead>
              <TableHead className="w-24">TIPO</TableHead>
              <TableHead>VÍNCULO</TableHead>
              <TableHead className="w-24">STATUS</TableHead>
              <TableHead className="w-28">FLAGS</TableHead>
              <TableHead className="w-28">EXPIRA EM</TableHead>
              {isDiretor && <TableHead className="w-36">AÇÕES</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-400">Nenhum usuário</TableCell></TableRow>
            )}
            {items.map((u) => {
              const exp = formatExpira(u.data_expiracao);
              return (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="font-medium text-sm">{u.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                </TableCell>
                <TableCell>
                  <Badge className={`${ROLE_BADGE[u.role] || "bg-zinc-100"} font-mono text-[10px]`}>{u.role?.toUpperCase()}</Badge>
                </TableCell>
                <TableCell className="text-xs">{(u.tipo || "interno").toUpperCase()}</TableCell>
                <TableCell className="text-xs">
                  {u.cliente_locativo_nome ? (
                    <div>
                      <div className="truncate max-w-xs">{u.cliente_locativo_nome}</div>
                      <div className="text-[10px] text-zinc-400 font-mono">{u.cliente_locativo_cnpj || "—"}</div>
                    </div>
                  ) : "—"}
                </TableCell>
                <TableCell>
                  <Badge className={u.ativo ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                    {u.ativo ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-[10px] space-y-0.5">
                  {u.senha_temporaria && <Badge className="bg-amber-100 text-amber-700 mr-1">SENHA TEMP</Badge>}
                  {u.permitir_google && <Badge variant="outline" className="text-[9px]">Google</Badge>}
                  {exp.expirada && <Badge className="bg-red-100 text-red-700">EXPIRADA</Badge>}
                </TableCell>
                <TableCell className={`text-xs ${exp.cls}`} data-testid={`expira-${u.id}`}>
                  {exp.alerta && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                  {exp.txt}
                </TableCell>
                {isDiretor && (
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditandoUsuario(u)} title="Editar usuário" data-testid={`editar-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-orange-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => resetSenha(u)} title="Reset senha" data-testid={`reset-${u.id}`}>
                        <Key className="w-3.5 h-3.5" />
                      </Button>
                      {u.senha_temporaria && (
                        <Button size="sm" variant="ghost" onClick={() => reenviarConvite(u)} title="Reenviar convite por e-mail" data-testid={`reenviar-${u.id}`}>
                          <Mail className="w-3.5 h-3.5 text-blue-500" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => renovarAcesso(u)} title="Renovar acesso (data de expiração)" data-testid={`renovar-${u.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 text-purple-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleStatus(u)} title={u.ativo ? "Desativar" : "Ativar"} data-testid={`toggle-${u.id}`}>
                        {u.ativo ? <ToggleRight className="w-3.5 h-3.5 text-emerald-600" /> : <ToggleLeft className="w-3.5 h-3.5 text-zinc-400" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => excluirUsuario(u)} title="Excluir definitivamente" data-testid={`excluir-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <div className="flex items-center justify-between text-sm text-zinc-600">
        <span>{total} usuários · página {page + 1} de {Math.max(1, Math.ceil(total / limit))}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>← Anterior</Button>
          <Button size="sm" variant="outline" disabled={(page + 1) * limit >= total} onClick={() => setPage(page + 1)}>Próxima →</Button>
        </div>
      </div>

      <ModalNovoUsuario open={novoOpen} onClose={() => setNovoOpen(false)} onCreated={carregar} usuariosExistentes={items} />
      <ModalNovoUsuario
        open={!!editandoUsuario}
        onClose={() => setEditandoUsuario(null)}
        onCreated={carregar}
        usuariosExistentes={items}
        usuarioEdicao={editandoUsuario}
      />
    </div>
  );
}
